import React, { useState, useRef, useEffect, forwardRef } from 'react';
import classNames from 'classnames';
import { SkeletonField } from 'components/layout/loading';
import InputLabel from '../InputLabel/InputLabel';
import InputError from '../InputError/InputError';
import { OPTION_TEXT_COLORS } from './constants';

const SelectField = forwardRef(({
  label,
  options = [],
  value,
  onChange,
  onBlur,
  errors,
  placeholder = 'Select an option',
  className = 'w-full',
  loading = false,
  disabled = false,
  name,
}, ref) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);
  const buttonRef = useRef(null);

  // Combine refs to ensure we can focus the button from outside (e.g. RHF error focus)
  useEffect(() => {
    if (ref) {
      if (typeof ref === 'function') {
        ref(buttonRef.current);
      } else {
        ref.current = buttonRef.current;
      }
    }
  }, [ref]);

  const selectedOption = options.find((opt) => opt.value === value);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSelect = (option) => {
    onChange(option.value);
    setIsOpen(false);
  };

  if (loading) {
    return <SkeletonField className={className} />;
  }

  const error = errors && errors[name];

  return (
    <div className={className} ref={containerRef}>
      {label && (
        <InputLabel htmlFor={name} className="mb-2">
          {label}
        </InputLabel>
      )}
      <div className="relative mt-2">
        <button
          ref={buttonRef}
          type="button"
          id={name}
          onClick={() => !disabled && setIsOpen(!isOpen)}
          onBlur={onBlur}
          disabled={disabled}
          className={classNames(
            "relative w-full rounded-md bg-white/5 py-1.5 pl-3 pr-10 text-left text-white shadow-sm ring-1 ring-inset ring-white/10 focus:outline-none focus:ring-2 focus:ring-indigo-500 sm:text-sm sm:leading-6",
            error && "ring-red-500 focus:ring-red-500",
            disabled ? 'opacity-50 cursor-not-allowed disabled:bg-gray-400' : 'cursor-default'
          )}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
        >
          <span
            className={classNames(
              "block truncate",
              !selectedOption && "text-gray-400",
              selectedOption && OPTION_TEXT_COLORS[selectedOption.badgeColor]
            )}
          >
            {selectedOption ? selectedOption.label : placeholder}
          </span>
          <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
            <svg className="h-5 w-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
              <path fillRule="evenodd" d="M10 3a.75.75 0 01.55.24l3.25 3.5a.75.75 0 11-1.1 1.02L10 4.852 7.3 7.76a.75.75 0 01-1.1-1.02l3.25-3.5A.75.75 0 0110 3zm-3.76 9.2a.75.75 0 011.06.04l2.7 2.908 2.7-2.908a.75.75 0 111.1 1.02l-3.25 3.5a.75.75 0 01-1.1 0l-3.25-3.5a.75.75 0 01.04-1.06z" clipRule="evenodd" />
            </svg>
          </span>
        </button>

        {isOpen && (
          <ul
            className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-gray-800 py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm"
            tabIndex="-1"
            role="listbox"
          >
            {options.map((option) => (
              <li
                key={option.value}
                className="relative cursor-default select-none py-2 pl-3 pr-9 text-white hover:bg-indigo-600 hover:text-white"
                role="option"
                aria-selected={value === option.value}
                onClick={() => handleSelect(option)}
              >
                <span
                  className={classNames(
                    "block truncate",
                    value === option.value && "font-semibold",
                    OPTION_TEXT_COLORS[option.badgeColor]
                  )}
                >
                  {option.label}
                </span>

                {value === option.value && (
                  <span className="absolute inset-y-0 right-0 flex items-center pr-4 text-indigo-400">
                    <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                      <path fillRule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clipRule="evenodd" />
                    </svg>
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
      {error && <InputError>{error.message}</InputError>}
    </div>
  );
});

export default SelectField;
